import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTenant } from './TenantContext';
import '../styles/platform-identity.css';

const API = process.env.REACT_APP_BACKEND_URL || '';
const EMPTY = { logo_url: null, favicon_url: null };
export const platformAssetUrl = value => typeof value === 'string' && /^\/api\/uploads\/platform\/[a-f0-9]{32}\.png$/.test(value) ? value : null;
const cleanBranding = data => ({ logo_url: platformAssetUrl(data?.logo_url), favicon_url: platformAssetUrl(data?.favicon_url) });
const Context = createContext({ branding: EMPTY, profile: null, refreshProfile: async () => {}, updateImage: () => {}, setAccount: () => {} });
export const usePlatformIdentity = () => useContext(Context);

export default function PlatformIdentityProvider({ children }) {
  const { isMainDomain, isLoading } = useTenant();
  const enabled = isMainDomain && !isLoading;
  const [branding, setBranding] = useState(EMPTY);
  const [profile, setProfile] = useState(null);
  const revision = useRef(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    const started = revision.current;
    fetch(`${API}/api/tenant/platform-branding`, { signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error('Branding unavailable'); return response.json(); })
      .then(data => { if (!controller.signal.aborted && revision.current === started) setBranding(cleanBranding(data)); })
      .catch(() => {}); // Existing default brand remains usable if unavailable.
    return () => controller.abort();
  }, [enabled]);
  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem('super_admin_token');
    const started = revision.current;
    if (!enabled || !token) { setProfile(null); return; }
    const response = await fetch(`${API}/api/tenant/super-admin/account`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) throw new Error('Account unavailable');
    const data = await response.json();
    if (!data?.profile || typeof data.profile.email !== 'string' || !data.branding) throw new Error('Account unavailable');
    if (localStorage.getItem('super_admin_token') === token && revision.current === started) {
      setProfile({ ...data.profile, avatar_url: platformAssetUrl(data.profile?.avatar_url) });
      setBranding(cleanBranding(data.branding));
    }
  }, [enabled]);
  const setAccount = useCallback(data => {
    revision.current += 1;
    setProfile({ ...data.profile, avatar_url: platformAssetUrl(data.profile?.avatar_url) });
    setBranding(cleanBranding(data.branding));
  }, []);
  const updateImage = useCallback((kind, url) => {
    revision.current += 1;
    const value = platformAssetUrl(url);
    if (kind === 'avatar') setProfile(current => current ? { ...current, avatar_url: value } : current);
    else setBranding(current => ({ ...current, [kind + '_url']: value }));
  }, []);
  useEffect(() => {
    if (!enabled || !branding.favicon_url) return undefined;
    let icon = document.head.querySelector('link[rel~="icon"]');
    const original = icon ? Array.from(icon.attributes).map(item => [item.name, item.value]) : null;
    if (!icon) { icon = document.createElement('link'); icon.rel = 'icon'; document.head.appendChild(icon); }
    icon.href = branding.favicon_url;
    icon.type = 'image/png';
    return () => {
      if (original) {
        Array.from(icon.attributes).forEach(item => icon.removeAttribute(item.name));
        original.forEach(([name, value]) => icon.setAttribute(name, value));
      } else icon.remove();
    };
  }, [enabled, branding.favicon_url]);
  return <Context.Provider value={{ branding: enabled ? branding : EMPTY, profile: enabled ? profile : null, refreshProfile, updateImage, setAccount }}>{children}</Context.Provider>;
}
